import { ARROW_DOWNWARD_PATH } from "@/components/ui/mui-arrow-icons";
import { muiGlyph } from "@/components/ui/mui-glyph";

// The glyphs the specifications screens' shared components draw, each path
// copied byte-for-byte from `@mui/icons-material` 9.2.0 (`<Name>.js`).
//
// Sizes are `SvgIcon`'s: `medium` is 1.5rem (24px), `small` 1.25rem (20px),
// `large` 2.1875rem (35px) (`SvgIcon.js:75-78`). A caller that needs another
// size passes it through `className`, which `cn` merges over the default.

/** `PrintOutlined.js` — the public header's Print button (#1022). MUI draws it
 *  as a path **and** a circle (the paper tray's light, `cx=18 cy=11.5 r=1`);
 *  the circle rides here as the same disc in two arcs, so the glyph stays the
 *  one-path body every ported icon shares. */
const PRINT_OUTLINED_PATH =
  "M19 8h-1V3H6v5H5c-1.66 0-3 1.34-3 3v6h4v4h12v-4h4v-6c0-1.66-1.34-3-3-3M8 5h8v3H8zm8 12v2H8v-4h8zm2-2v-2H6v2H4v-4c0-.55.45-1 1-1h14c.55 0 1 .45 1 1v4zM18 10.5a1 1 0 1 0 0 2a1 1 0 1 0 0-2";
/** `HubOutlined.js` — the Core Specifications tab. */
const HUB_OUTLINED_PATH =
  "M21 6.5c-1.66 0-3 1.34-3 3 0 .07 0 .14.01.21l-2.03.68c-.64-1.21-1.82-2.09-3.22-2.32V5.91C14.04 5.57 15 4.4 15 3c0-1.66-1.34-3-3-3S9 1.34 9 3c0 1.4.96 2.57 2.25 2.91v2.16c-1.4.23-2.58 1.11-3.22 2.32l-2.04-.68C6 9.64 6 9.57 6 9.5c0-1.66-1.34-3-3-3s-3 1.34-3 3 1.34 3 3 3c1.06 0 1.98-.55 2.52-1.37l2.03.68c-.2 1.29.17 2.66 1.09 3.69l-1.41 1.77Q6.66 17 6 17c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3c0-.68-.22-1.3-.6-1.8l1.41-1.77c1.36.76 3.02.75 4.37 0l1.41 1.77c-.37.5-.59 1.12-.59 1.8 0 1.66 1.34 3 3 3s3-1.34 3-3-1.34-3-3-3c-.44 0-.85.09-1.23.26l-1.41-1.77c.93-1.04 1.29-2.4 1.09-3.69l2.03-.68c.53.82 1.46 1.37 2.52 1.37 1.66 0 3-1.34 3-3S22.66 6.5 21 6.5m-18 4c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1M6 21c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1m5-18c0-.55.45-1 1-1s1 .45 1 1-.45 1-1 1-1-.45-1-1m1 12c-1.38 0-2.5-1.12-2.5-2.5S10.62 10 12 10s2.5 1.12 2.5 2.5S13.38 15 12 15m6 4c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1m3-8.5c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1";
/** `HomeOutlined.js` — the Base Specifications tab. */
const HOME_OUTLINED_PATH = "m12 5.69 5 4.5V18h-2v-6H9v6H7v-7.81zM12 3 2 12h3v8h6v-6h2v6h6v-8h3z";
/** `HolidayVillageOutlined.js` — the Community Specifications tab. */
const HOLIDAY_VILLAGE_OUTLINED_PATH =
  "m8 4-6 6v10h12V10zm4 14H9v-3H7v3H4v-7.17l4-4 4 4zm-3-5H7v-2h2zm9 7V8.35L13.65 4h-2.83L16 9.18V20zm4 0V6.69L19.31 4h-2.83L20 7.52V20z";
/** `DragIndicator.js` — the sort row's handle, at `fontSize='small'`. */
const DRAG_INDICATOR_PATH =
  "M11 18c0 1.1-.9 2-2 2s-2-.9-2-2 .9-2 2-2 2 .9 2 2m-2-8c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2m0-6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2m6 4c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2m0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2m0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2";
/** `List.js` — `EmptyTable`'s glyph, at the donor's `fontSize: 53`. */
const LIST_PATH = "M3 13h2v-2H3zm0 4h2v-2H3zm0-8h2V7H3zm4 4h14v-2H7zm0 4h14v-2H7zM7 7v2h14V7z";
/** `ChevronLeft.js` / `ChevronRight.js` — MRT's default pager icons. */
const CHEVRON_LEFT_PATH = "M15.41 7.41 14 6l-6 6 6 6 1.41-1.41L10.83 12z";
const CHEVRON_RIGHT_PATH = "M10 6 8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z";

/** `AddHomeOutlined.js` — a core row's create-base button. Two paths in the
 *  source, joined here: the factory draws one, and SVG path data concatenates. */
const ADD_HOME_OUTLINED_PATH =
  "m12 5.5 6 4.5v1c.7 0 1.37.1 2 .29V9l-8-6-8 6v12h7.68c-.3-.62-.5-1.29-.6-2H6v-9zM18 13c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5m3 5.5h-2.5V21h-1v-2.5H15v-1h2.5V15h1v2.5H21z";
/** `Edit.js` — the row's pencil. */
const EDIT_PATH =
  "M3 17.25V21h3.75L17.81 9.94l-3.75-3.75zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34a.996.996 0 0 0-1.41 0l-1.83 1.83 3.75 3.75z";
/** `Abc.js` — the row menu's Edit Name. */
const ABC_PATH =
  "M21 11h-1.5v-.5h-2v3h2V13H21v1c0 .55-.45 1-1 1h-3c-.55 0-1-.45-1-1v-4c0-.55.45-1 1-1h3c.55 0 1 .45 1 1zM8 10v5H6.5v-1.5h-2V15H3v-5c0-.55.45-1 1-1h3c.55 0 1 .45 1 1m-1.5.5h-2V12h2zm7 1.5c.55 0 1 .45 1 1v1c0 .55-.45 1-1 1h-4V9h4c.55 0 1 .45 1 1v1c0 .55-.45 1-1 1M11 10.5v.75h2v-.75zm2 2.25h-2v.75h2z";
/** `ContentCopy.js` — Duplicate. */
const CONTENT_COPY_PATH =
  "M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2m0 16H8V7h11z";
/** `ArchiveOutlined.js` — Archive and Enable. */
const ARCHIVE_OUTLINED_PATH =
  "m20.54 5.23-1.39-1.68C18.88 3.21 18.47 3 18 3H6c-.47 0-.88.21-1.16.55L3.46 5.23C3.17 5.57 3 6.02 3 6.5V19c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6.5c0-.48-.17-.93-.46-1.27M6.24 5h11.52l.81.97H5.44zM5 19V8h14v11zm8.45-9h-2.9v3H8l4 4 4-4h-2.55z";
/** `DeleteOutlineOutlined.js` — Delete. */
const DELETE_OUTLINE_OUTLINED_PATH =
  "M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6zM8 9h8v10H8zm7.5-5-1-1h-5l-1 1H5v2h14V4z";
/** `InfoOutlined.js` — the column headers' info glyph, at `fontSize='small'`.
 *  Not the alert's `internal/svg-icons/InfoOutlined`, which is another path. */
const INFO_OUTLINED_PATH =
  "M11 7h2v2h-2zm0 4h2v6h-2zm1-9C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2m0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8";
/** `ArrowUpward.js` / `ArrowDownward.js` (the latter's path shared from
 *  `mui-arrow-icons`) — the sort row's direction toggle. */
const ARROW_UPWARD_PATH = "m4 12 1.41 1.41L11 7.83V20h2V7.83l5.58 5.59L20 12l-8-8z";
/** `CheckCircleOutlined.js` — the list's success snackbar icon. */
const CHECK_CIRCLE_OUTLINED_PATH =
  "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2m0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8m4.59-12.42L10 14.17l-2.59-2.58L6 13l4 4 8-8z";
/** `Clear.js` at `fontSize='small'` — the sort row's remove button. */
const CLEAR_PATH =
  "M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z";

export const MuiHubOutlinedIcon = muiGlyph("mui-hub-outlined", HUB_OUTLINED_PATH, "size-6");
export const MuiHomeOutlinedIcon = muiGlyph("mui-home-outlined", HOME_OUTLINED_PATH, "size-6");
export const MuiHolidayVillageOutlinedIcon = muiGlyph(
  "mui-holiday-village-outlined",
  HOLIDAY_VILLAGE_OUTLINED_PATH,
  "size-6",
);
export const MuiDragIndicatorIcon = muiGlyph("mui-drag-indicator", DRAG_INDICATOR_PATH, "size-5");
export const MuiListIcon = muiGlyph("mui-list", LIST_PATH, "size-[53px]");
export const MuiChevronLeftIcon = muiGlyph("mui-chevron-left", CHEVRON_LEFT_PATH, "size-6");
export const MuiChevronRightIcon = muiGlyph("mui-chevron-right", CHEVRON_RIGHT_PATH, "size-6");
export const MuiAddHomeOutlinedIcon = muiGlyph(
  "mui-add-home-outlined",
  ADD_HOME_OUTLINED_PATH,
  "size-6",
);
export const MuiEditIcon = muiGlyph("mui-edit", EDIT_PATH, "size-6");
export const MuiAbcIcon = muiGlyph("mui-abc", ABC_PATH, "size-6");
export const MuiContentCopyIcon = muiGlyph("mui-content-copy", CONTENT_COPY_PATH, "size-6");
export const MuiArchiveOutlinedIcon = muiGlyph(
  "mui-archive-outlined",
  ARCHIVE_OUTLINED_PATH,
  "size-6",
);
export const MuiDeleteOutlineOutlinedIcon = muiGlyph(
  "mui-delete-outline-outlined",
  DELETE_OUTLINE_OUTLINED_PATH,
  "size-6",
);

/** `MonetizationOnOutlined.js` — the Spec header's Where to save button. */
const MONETIZATION_ON_OUTLINED_PATH =
  "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2m0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8m.31-8.86c-1.77-.45-2.34-.94-2.34-1.67 0-.84.79-1.43 2.1-1.43 1.38 0 1.9.66 1.94 1.64h1.71c-.05-1.34-.87-2.57-2.49-2.97V5H10.9v1.69c-1.51.32-2.72 1.3-2.72 2.81 0 1.79 1.49 2.69 3.66 3.21 1.95.46 2.34 1.15 2.34 1.87 0 .53-.39 1.39-2.1 1.39-1.6 0-2.23-.72-2.32-1.64H8.04c.1 1.7 1.36 2.66 2.86 2.97V19h2.34v-1.67c1.52-.29 2.72-1.16 2.73-2.77-.01-2.2-1.9-2.96-3.66-3.42";
/**
 * `ArrowDropUp.js` — the Specification Notes toggle, open. Its closed glyph is
 * `mui-arrow-icons.tsx`'s `ArrowDropDown`; both at the default 24px, which the
 * small `IconButton`'s 18px does not reach (`SvgIcon` sets its own size).
 */
const ARROW_DROP_UP_PATH = "m7 14 5-5 5 5z";
/**
 * `LinkOff.js` (`@mui/icons-material` 9.2.0) — Core Spec Links' Unlink button
 * (#1085). Its 18px is the small button's start-icon slot's, not its own.
 */
const LINK_OFF_PATH =
  "M17 7h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1 0 1.43-.98 2.63-2.31 2.98l1.46 1.46C20.88 15.61 22 13.95 22 12c0-2.76-2.24-5-5-5m-1 4h-2.19l2 2H16zM2 4.27l3.11 3.11C3.29 8.12 2 9.91 2 12c0 2.76 2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1 0-1.59 1.21-2.9 2.76-3.07L8.73 11H8v2h2.73L13 15.27V17h1.73l4.01 4L20 19.74 3.27 3z";

export const MuiMonetizationOnOutlinedIcon = muiGlyph(
  "mui-monetization-on-outlined",
  MONETIZATION_ON_OUTLINED_PATH,
  "size-[18px]",
);
export const MuiArrowDropUpIcon = muiGlyph("mui-arrow-drop-up", ARROW_DROP_UP_PATH, "size-6");
export const MuiLinkOffIcon = muiGlyph("mui-link-off", LINK_OFF_PATH, "size-6");
export const MuiInfoOutlinedSmallIcon = muiGlyph(
  "mui-info-outlined-small",
  INFO_OUTLINED_PATH,
  "size-5",
);
export const MuiArrowUpwardSmallIcon = muiGlyph(
  "mui-arrow-upward-small",
  ARROW_UPWARD_PATH,
  "size-5",
);
/** `ArrowUpwardOutlined.js`, the same path, at its 24px: "Where to save"'s
 *  share chip (#1217). */
export const MuiArrowUpwardIcon = muiGlyph("mui-arrow-upward", ARROW_UPWARD_PATH, "size-6");
export const MuiArrowDownwardSmallIcon = muiGlyph(
  "mui-arrow-downward-small",
  ARROW_DOWNWARD_PATH,
  "size-5",
);
export const MuiCheckCircleOutlinedIcon = muiGlyph(
  "mui-check-circle-outlined",
  CHECK_CIRCLE_OUTLINED_PATH,
  "h-[1em] w-[1em]",
);
export const MuiClearSmallIcon = muiGlyph("mui-clear-small", CLEAR_PATH, "size-5");

/** `DeleteForeverOutlined.js` — the hero box's deleted-selection and Retired
 *  marks, at the donor's `size='16px'` (`HeroImageBox.tsx:19-33`). */
const DELETE_FOREVER_OUTLINED_PATH =
  "M14.12 10.47 12 12.59l-2.13-2.12-1.41 1.41L10.59 14l-2.12 2.12 1.41 1.41L12 15.41l2.12 2.12 1.41-1.41L13.41 14l2.12-2.12zM15.5 4l-1-1h-5l-1 1H5v2h14V4zM6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6zM8 9h8v10H8z";
/** `ErrorOutlineOutlined.js` — the hero box's missing-image mark, 16px. Not the
 *  alert's `ErrorOutline`, which is another drawing. */
const ERROR_OUTLINE_OUTLINED_PATH =
  "M11 15h2v2h-2zm0-8h2v6h-2zm.99-5C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2M12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8";

export const MuiDeleteForeverOutlinedIcon = muiGlyph(
  "mui-delete-forever-outlined",
  DELETE_FOREVER_OUTLINED_PATH,
  "size-4",
);
export const MuiErrorOutlineOutlinedIcon = muiGlyph(
  "mui-error-outline-outlined",
  ERROR_OUTLINE_OUTLINED_PATH,
  "size-4",
);
/** `InfoOutlined.js` at the default 24px — the changed-from-base row's mark
 *  (`ChangedFromBase.tsx`). */
export const MuiInfoOutlinedMediumIcon = muiGlyph(
  "mui-info-outlined",
  INFO_OUTLINED_PATH,
  "size-6",
);

/** `FilterList.js` at `fontSize='small'` — an idle column filter's funnel
 *  (`ColumnHeaderWithFilter.tsx`). */
const FILTER_LIST_PATH = "M10 18h4v-2h-4zM3 6v2h18V6zm3 7h12v-2H6z";
/** `FilterAlt.js` at `fontSize='small'` — the funnel of a column with a filter on. */
const FILTER_ALT_PATH =
  "M4.25 5.61C6.27 8.2 10 13 10 13v6c0 .55.45 1 1 1h2c.55 0 1-.45 1-1v-6s3.72-4.8 5.74-7.39c.51-.66.04-1.61-.79-1.61H5.04c-.83 0-1.3.95-.79 1.61";

export const MuiFilterListSmallIcon = muiGlyph("mui-filter-list", FILTER_LIST_PATH, "size-5");
export const MuiFilterAltSmallIcon = muiGlyph("mui-filter-alt", FILTER_ALT_PATH, "size-5");

/** `Dehaze.js` at the donor's `fontSize: 16` — a group card's and a line row's
 *  drag mark, on hover and while lifted (`EditableGroup.tsx`,
 *  `draggable-row.tsx`). */
const DEHAZE_PATH = "M2 15.5v2h20v-2zm0-5v2h20v-2zm0-5v2h20v-2z";
export const MuiDehazeIcon = muiGlyph("mui-dehaze", DEHAZE_PATH, "size-4");

/** `Lock.js` at the default 24px — a finalized line's lock (`LineLock.tsx`). */
const LOCK_PATH =
  "M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2m-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2m3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1s3.1 1.39 3.1 3.1z";
export const MuiLockIcon = muiGlyph("mui-lock", LOCK_PATH, "size-6");

/** `Delete.js` at `fontSize='small'` — the group menu's Delete item
 *  (`MoreGroupMenu.tsx`). */
const DELETE_PATH = "M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6zM19 4h-3.5l-1-1h-5l-1 1H5v2h14z";
export const MuiDeleteSmallIcon = muiGlyph("mui-delete-small", DELETE_PATH, "size-5");
export const MuiPrintOutlinedIcon = muiGlyph("mui-print-outlined", PRINT_OUTLINED_PATH, "size-6");
